import type { ColumnDef, SortingState } from '@tanstack/react-table';
import axios from 'axios';
import cx from 'clsx';
import { dateToLocalYMDHM, estimateEtaSeconds, secondsToTimeSpan } from 'common/date.ts';
import dayjs from 'common/dayjs.ts';
import { GET, isSuperadmin, MPOST, MPUT, POST } from 'common/helpers.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { PermissionParsed } from 'types/Permission.ts';
import type { REASSIGN_JOB_STATUS, ReassignJob } from 'types/ReassignJob.ts';
import type { Role as TRole } from 'types/Role.ts';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { ICircle, IDelete, IDeleteOutline, IRefresh } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { AddRolePermissionForm } from '#admin/component/AddRolePermissionForm.tsx';
import { DeleteDialog } from '#admin/component/DeleteDialog.tsx';
import { useAlert, useConfirm } from '#admin/component/DialogProvider.tsx';
import { Table } from '#admin/component/table/index.ts';
import { useMe } from '#admin/hook/useMe.tsx';

type ReassignStatus = { active: ReassignJob | null; recent: ReassignJob[] };

const REASSIGN_STATUS_COLOR: Record<REASSIGN_JOB_STATUS, string> = {
  pending: 'text-blue',
  running: 'text-blue',
  done: 'text-green',
  failed: 'text-red',
  cancelled: 'text-grey-label',
};

export const getColumns = (t: (key: string) => string, knownUsers: User[], onDelete: (permission: PermissionParsed & { unparsed_template: string }) => void) => {
  const columns: ColumnDef<PermissionParsed & { unparsed_template: string }>[] = [
    {
      accessorKey: 'subject',
      header: t('views.role.columns.subject'),
      size: 100,
      cell: (info) => {
        const { subject, subject_is_group } = info.row.original;
        const value = subject + (subject_is_group ? '/*' : '');
        return value;
      },
    },
    {
      accessorKey: 'operation',
      header: t('views.role.columns.operation'),
      size: 200,
    },
    {
      accessorKey: 'resource',
      header: t('views.role.columns.resource'),
      size: 350,
      cell(info) {
        const { resource, resource_is_owner } = info.row.original;
        const resourceToUser = knownUsers.find((user) => user.id === resource)?.name ?? resource;
        const value = resourceToUser + (resource_is_owner ? '/*' : '');
        return value;
      },
    },
    {
      id: 'actions',
      header: t('views.role.columns.actions'),
      size: 70,
      enableSorting: false,
      cell: (info) => (
        <div className="flex w-full items-center justify-center">
          <IDelete
            className="text-gray-500"
            size={20}
            onClick={() => {
              if (onDelete) onDelete(info.row.original);
            }}
          />
        </div>
      ),
    },
  ];
  return columns;
};

export const Role = () => {
  const { t } = useTranslation();
  const { id: roleId } = useParams();
  const { data: users } = useSWR<User[]>(['/user/list', { sort: [['name', 'asc']] }], POST);
  const { data: role, mutate: mutateRole, isLoading } = useSWR<TRole>([`/role/${roleId}`], GET, { keepPreviousData: true });
  const updateRoleMutation = useSWRMutation(role && `/role/${role.id}`, MPUT);

  const [sorters, setSorters] = React.useState<SortingState>([{ id: 'operation', desc: false }]);
  const [pendingDeletePermission, setPendingDeletePermission] = React.useState<(PermissionParsed & { unparsed_template: string }) | null>(null);
  const [isDeleteRoleOpen, setIsDeleteRoleOpen] = React.useState(false);
  const confirm = useConfirm();
  const alert = useAlert();
  const { me } = useMe();

  const reassignAllMutation = useSWRMutation('/role/reassign-all', MPOST);
  const cancelReassignMutation = useSWRMutation(role ? `/role/${role.id}/reassign-all/cancel` : null, MPOST);

  // Fetched on mount so history shows even when idle; only polls while a job is active.
  const { data: reassignStatus, mutate: mutateReassignStatus } = useSWR<ReassignStatus>(role ? [`/role/${role.id}/reassign-all/status`] : null, POST, {
    refreshInterval: (data) => (data?.active ? 2000 : 0),
  });
  const activeJob = reassignStatus?.active ?? null;
  const recentJobs = reassignStatus?.recent ?? [];
  const etaSeconds = activeJob ? estimateEtaSeconds(activeJob.started_at, activeJob.processed, activeJob.total) : null;

  const rows = React.useMemo(() => {
    if (!role) return [];
    const rows = role.permission_templates_parsed.map((parsed_permission, i) => ({ ...parsed_permission, unparsed_template: role.permission_templates[i] }));
    return rows;
  }, [role]);

  const onPermissionDelete = React.useCallback((permission: PermissionParsed & { unparsed_template: string }) => {
    setPendingDeletePermission(permission);
  }, []);

  const handleDeletePermissionConfirm = React.useCallback(async () => {
    if (!role || !pendingDeletePermission) return;
    setPendingDeletePermission(null);
    await updateRoleMutation.trigger({
      id: role.id,
      permission_templates: btoa(JSON.stringify(role.permission_templates.filter((template) => template !== pendingDeletePermission.unparsed_template))),
    });
    void mutateRole();
  }, [role, pendingDeletePermission, updateRoleMutation, mutateRole]);

  const columns = React.useMemo(() => getColumns(t, users || [], onPermissionDelete), [t, users, onPermissionDelete]);

  const handleDeleteRoleConfirm = React.useCallback(async () => {
    setIsDeleteRoleOpen(false);
    await axios.delete(`/role/${roleId}`);
    window.location.href = '/roles';
  }, [roleId]);

  const handleReassignAll = React.useCallback(async () => {
    if (!role) return;
    if (!(await confirm(t('views.role.confirmMessages.reassignAll')))) return;
    try {
      await reassignAllMutation.trigger({ name: role.name });
    } catch (error: any) {
      const code = error?.response?.data?.code;
      if (code === 'ROLE_REASSIGN_IN_PROGRESS') alert(t('views.role.reassign.toasts.inProgress'));
      else if (code === 'REASSIGN_QUEUE_FULL') alert(t('views.role.reassign.toasts.queueFull'));
      else throw error;
    }
    void mutateReassignStatus();
  }, [role, reassignAllMutation, mutateReassignStatus, t, confirm, alert]);

  const handleCancelReassign = React.useCallback(async () => {
    if (!role) return;
    try {
      await cancelReassignMutation.trigger({});
    } catch (error: any) {
      const code = error?.response?.data?.code;
      if (code === 'CANNOT_CANCEL_RUNNING_REASSIGN') alert(t('views.role.reassign.toasts.cancelFailedRunning'));
      else if (!code) throw error;
    }
    void mutateReassignStatus();
  }, [role, cancelReassignMutation, mutateReassignStatus, t, alert]);

  const handleAddPermission = async (values: PermissionParsed & { unparsed_permission: string }) => {
    if (!role) return;
    const newPermissionTemplates = Array.from(new Set([...role.permission_templates, values.unparsed_permission]));
    await updateRoleMutation.trigger({ id: role.id, permission_templates: btoa(JSON.stringify(newPermissionTemplates)) });
    void mutateRole();
  };

  if (!me || isLoading || !role) return <Spinner centered={true} />;

  return (
    <main className="view-container">
      <DeleteDialog
        isOpen={!!pendingDeletePermission}
        onClose={() => setPendingDeletePermission(null)}
        onConfirm={handleDeletePermissionConfirm}
        title={t('views.role.confirmMessages.deletePermissionTitle')}
        message={t('views.role.confirmMessages.deletePermission')}
      />
      <DeleteDialog
        isOpen={isDeleteRoleOpen}
        onClose={() => setIsDeleteRoleOpen(false)}
        onConfirm={handleDeleteRoleConfirm}
        title={t('views.role.buttons.deleteRole')}
        message={t('views.role.confirmMessages.deleteRole')}
      />
      <section className="flex flex-row place-content-between items-baseline">
        <h4 className="flex flex-col gap-2">
          <span>{role.name}</span>
          {role.description && <span className="text-grey-label text-sm">{role.description}</span>}
        </h4>

        <div className="flex flex-row gap-4">
          {isSuperadmin(me) && (
            <Button className="blue-outlined" LeftIcon={IRefresh} onClick={handleReassignAll} loading={reassignAllMutation.isMutating} disabled={!!activeJob}>
              {t('views.role.buttons.reassignAll')}
            </Button>
          )}

          {isSuperadmin(me) && (
            <Button className="red-outlined" LeftIcon={IDeleteOutline} onClick={() => setIsDeleteRoleOpen(true)}>
              {t('views.role.buttons.deleteRole')}
            </Button>
          )}
        </div>
      </section>

      {(activeJob || recentJobs.length > 0) && (
        <section className="mt-4 flex flex-col gap-3 bg-white p-8 py-4" aria-live="polite">
          <h6>{t('views.role.reassign.sectionTitle')}</h6>

          {activeJob && activeJob.status === 'pending' && (
            <div className="flex items-center gap-4">
              <span>{t('views.role.reassign.queued')}</span>
              <Button small className="red-outlined w-min" onClick={handleCancelReassign} loading={cancelReassignMutation.isMutating}>
                {t('views.role.reassign.cancel')}
              </Button>
            </div>
          )}

          {activeJob && activeJob.status === 'running' && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-sm">
                <span className="tabular-nums">
                  {activeJob.total > 0
                    ? t('views.role.reassign.progress', {
                        pct: Math.floor((activeJob.processed / activeJob.total) * 100),
                        processed: activeJob.processed,
                        total: activeJob.total,
                      })
                    : t('views.role.reassign.preparing')}
                </span>
                <span className="text-grey-label">
                  {etaSeconds !== null ? t('views.role.reassign.etaLeft', { eta: secondsToTimeSpan(etaSeconds) }) : t('views.role.reassign.estimating')}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded bg-grey-border">
                <div
                  className="h-full rounded bg-blue transition-[width] duration-300 ease-out motion-reduce:transition-none"
                  style={{ width: `${activeJob.total > 0 ? Math.floor((activeJob.processed / activeJob.total) * 100) : 0}%` }}
                />
              </div>
              {activeJob.started_at && <span className="text-grey-label text-xs">{t('views.role.reassign.startedAgo', { ago: dayjs(activeJob.started_at).fromNow() })}</span>}
            </div>
          )}

          {recentJobs.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="font-bold text-sm">{t('views.role.reassign.recentTitle')}</span>
              {recentJobs.map((job) => {
                const by = (users || []).find((user) => user.id === job.triggered_by)?.name;
                return (
                  <div key={job.id} className="flex items-center gap-2 text-grey-label text-sm tabular-nums">
                    <ICircle size={10} className={REASSIGN_STATUS_COLOR[job.status]} />
                    <span>{t(`views.role.reassign.statuses.${job.status}`)}</span>
                    <span>
                      {job.processed}/{job.total}
                    </span>
                    {by && <span>{t('views.role.reassign.byUser', { name: by })}</span>}
                    <span>{dateToLocalYMDHM(job.finished_at || job.created_at)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <section className="mt-4 bg-white p-8 pb-4">
        <h6>{t('views.role.permissionTemplates')}</h6>

        <AddRolePermissionForm onAdd={handleAddPermission} onAddLoading={updateRoleMutation.isMutating} />

        <Table
          className={cx('max-h-[calc(100vh-385px)]')}
          columns={columns}
          items={rows}
          rowsCount={rows.length}
          defaultSorting={sorters}
          onColumnSort={setSorters}
          estimateSize={50}
          overscan={50}
        />
      </section>
    </main>
  );
};
